import { Header } from '@/components/layout/Header';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageMetadata } from '@/hooks/usePageMetadata';
import { ROUTES } from '@/constants';
import {
  Container,
  Typography,
  Paper,
  Box,
  MenuItem,
  ListItemText,
} from '@gapartners/ui';
import { MenuList } from '@gapartners/ui/menu-list';

const MenuItemLink = MenuItem as React.ComponentType<
  React.ComponentProps<typeof MenuItem> & {
    component?: React.ElementType;
    to?: string;
  }
>;

type SidebarTheme = {
  palette: {
    mode: string;
    text: { primary: string };
    primary: { main: string };
  };
};

const sidebarMenuItemSx = {
  py: 1.5,
  borderLeft: '3px solid transparent',
  color: 'text.primary',
  '& .MuiListItemText-primary': {
    color: 'inherit',
  },
  '&:hover': {
    bgcolor: 'action.hover',
  },
  '&.Mui-selected, &.active, &.Mui-selected.active': {
    bgcolor: 'action.selected',
    borderLeftColor: 'primary.main',
    color: (theme: SidebarTheme) =>
      theme.palette.mode === 'dark'
        ? theme.palette.text.primary
        : theme.palette.text.primary,
    fontWeight: 600,
    '& .MuiListItemText-primary': {
      color: (theme: SidebarTheme) =>
        theme.palette.mode === 'dark'
          ? theme.palette.text.primary
          : theme.palette.text.primary,
      fontWeight: 600,
    },
    '&:hover': {
      bgcolor: 'action.selected',
    },
  },
  '&.Mui-focusVisible': {
    bgcolor: 'action.hover',
  },
  '&.Mui-disabled': {
    color: 'text.disabled',
    borderLeftColor: 'transparent',
  },
};

export function ManagerPage() {
  const { t } = useTranslation();
  const location = useLocation();

  // Set page metadata
  usePageMetadata({
    title: t('pages.manager.title'),
    description: t('pages.manager.description'),
  });

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Header />

      {/* Page Header */}
      <Paper
        variant="outlined"
        square
        sx={{
          px: 0,
          py: { xs: 1, sm: 1.25 },
          bgcolor: 'action.hover',
          borderBottom: '1px solid',
          borderColor: 'divider',
          borderLeft: 0,
          borderRight: 0,
          borderTop: 0,
          borderRadius: 0,
        }}
      >
        <Container maxWidth="xl">
          <Typography
            variant="h5"
            fontWeight={600}
            color="text.primary"
            sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, mb: 0.5 }}
          >
            {t('pages.manager.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
            {t('pages.manager.description')}
          </Typography>
        </Container>
      </Paper>

      {/* Content Area */}
      <Container
        maxWidth="xl"
        sx={{ py: { xs: 3, sm: 4 }, px: { xs: 2, sm: 3 } }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '240px 1fr' },
            gap: { xs: 2, sm: 3 },
            alignItems: 'start',
          }}
        >
          {/* Sidebar */}
          <Paper variant="outlined" sx={{ p: 0, position: 'sticky', top: 16 }}>
            <MenuList disablePadding>
              <MenuItemLink
                component={NavLink}
                to={ROUTES.MANAGER.TEAM}
                selected={
                  location.pathname === ROUTES.MANAGER.ROOT ||
                  location.pathname.endsWith(`/${ROUTES.MANAGER.TEAM}`)
                }
                sx={sidebarMenuItemSx}
              >
                <ListItemText primary={t('pages.manager.navigation.team')} />
              </MenuItemLink>
              <MenuItemLink
                component={NavLink}
                to={ROUTES.MANAGER.PERFORMANCE}
                selected={location.pathname.endsWith(
                  `/${ROUTES.MANAGER.PERFORMANCE}`
                )}
                sx={sidebarMenuItemSx}
              >
                <ListItemText
                  primary={t('pages.manager.navigation.performance')}
                />
              </MenuItemLink>
              <MenuItemLink
                component={NavLink}
                to={`${ROUTES.MANAGER.ROOT}/quality-metrics`}
                selected={location.pathname.includes('quality-metrics')}
                sx={sidebarMenuItemSx}
              >
                <ListItemText
                  primary={t('pages.manager.navigation.qualityMetrics')}
                />
              </MenuItemLink>
              <MenuItemLink
                component={NavLink}
                to={ROUTES.MANAGER.CALL_HISTORY}
                selected={location.pathname.endsWith(
                  `/${ROUTES.MANAGER.CALL_HISTORY}`
                )}
                sx={sidebarMenuItemSx}
              >
                <ListItemText
                  primary={t('pages.manager.navigation.callHistory')}
                />
              </MenuItemLink>
            </MenuList>
          </Paper>

          {/* Main Content */}
          <Box>
            <Outlet />
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
